import { useEffect, useMemo, useState } from "react";
import { FiSearch, FiEye, FiX, FiFileText, FiDownload } from "react-icons/fi";
import { FaPills } from "react-icons/fa";
import { message } from "antd";
import PharmacyService from "../../../../Services/PharmacyService";

function parseVitalDate(dt) {
  if (!dt) return null;
  const d = new Date(String(dt).replace(" ", "T"));
  return isNaN(d.getTime()) ? null : d;
}

function formatDate(dt) {
  const d = parseVitalDate(dt);
  if (!d) return "-";
  return d.toLocaleDateString("en-GB", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
}

export default function Pharmacy({ patientId, admissionId, patientDetails }) {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(false);
  const [dateFilter, setDateFilter] = useState("");
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedOrder, setSelectedOrder] = useState(null);

  const effectivePatientId = patientId || patientDetails?.patientId;

  const fetchPharmacyOrders = async () => {
    if (!effectivePatientId) return;
    setLoading(true);
    const payload = {
      patientId: effectivePatientId,
      detailsYN: "Y",
      drugIpdStatus: "Confirm",
      pageNo: 1,
      pageSize: 10,
    };
    try {
      const response = await PharmacyService.PharmacyGet(payload);
      console.log("Pharmacy orders response:", response);
      const list = Array.isArray(response?.data)
        ? response.data
        : Array.isArray(response?.data?.data)
        ? response.data.data
        : Array.isArray(response?.data?.dispensedDrugsHdrList)
        ? response.data.dispensedDrugsHdrList
        : [];
      setOrders(list);
    } catch (e) {
      console.error("Error fetching pharmacy orders:", e);
      setOrders([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchPharmacyOrders();
  }, [effectivePatientId]);

  const getFileUrl = (fileName) => {
    if (!fileName) return "";
    if (fileName.startsWith("http://") || fileName.startsWith("https://")) {
      return fileName;
    }
    const baseUrl =
      process.env.REACT_APP_IMAGE_URL ||
      "https://shadow-nonprod-application-data.s3.us-east-2.amazonaws.com/";
    return `${baseUrl}${fileName}`;
  };

  const handleOpenFile = (fileName) => {
    const url = getFileUrl(fileName);
    if (!url) {
      message.error("No file found!!");
      return;
    }
    window.open(url, "_blank");
  };

  const handleViewOrder = (order) => {
    setSelectedOrder(order);
  };

  const filteredOrders = useMemo(() => {
    return (orders || []).filter((item) => {
      if (searchTerm) {
        const term = searchTerm.toLowerCase();
        const drugNames = Array.isArray(item?.dispensedDrugsDtl)
          ? item.dispensedDrugsDtl
              .map((d) => d.drugName || d.medicineName || "")
              .join(" ")
              .toLowerCase()
          : "";
        const matchesName =
          (item?.dispensedId || "").toLowerCase().includes(term) ||
          (item?.orderId || "").toLowerCase().includes(term) ||
          (item?.drugName || "").toLowerCase().includes(term) ||
          drugNames.includes(term);
        const matchesDoctor = (
          item?.userName ||
          item?.doctorName ||
          item?.orderedBy ||
          ""
        )
          .toLowerCase()
          .includes(term);
        if (!matchesName && !matchesDoctor) return false;
      }
      if (dateFilter) {
        const rawDate =
          item?.createdDate ||
          item?.dispensedDate ||
          item?.uploadedDateTime ||
          "";
        const itemDate = formatDate(rawDate);
        const selDate = formatDate(dateFilter);
        if (
          itemDate !== selDate &&
          !String(rawDate).includes(dateFilter)
        ) {
          return false;
        }
      }
      return true;
    });
  }, [orders, searchTerm, dateFilter]);

  return (
    <div className="rounded-lg border bg-white">
      {/* Header */}
      <div className="flex items-center justify-between border-b p-4">
        <div className="flex items-center gap-2">
          <FaPills className="text-[#3b3f97] text-base" />
          <h2 className="text-sm font-semibold text-[#3b3f97]">
            Pharmacy Orders
          </h2>
          {!loading && orders.length > 0 && (
            <span className="text-xs font-semibold text-indigo-700 bg-indigo-50 border border-indigo-100 px-2 py-0.5 rounded-full">
              {orders.length}
            </span>
          )}
        </div>
        <div className="flex gap-3">
          <input
            type="date"
            value={dateFilter}
            onChange={(e) => setDateFilter(e.target.value)}
            className="h-8 rounded-md border border-[#e5e7eb] bg-white px-4 text-[13px] font-semibold text-[#111827] outline-none focus:border-[#5b5bd6]"
          />
          <div className="relative">
            <FiSearch className="absolute left-3 top-2.5 text-gray-400" />
            <input
              type="text"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder="Search orders, medicines..."
              className="rounded-lg border py-2 pl-9 pr-3 text-xs outline-none"
            />
          </div>
        </div>
      </div>

      {/* Table */}
      <div className="overflow-x-auto">
        <table className="min-w-full text-xs">
          <thead className="bg-gray-50 text-gray-700">
            <tr>
              <th className="p-3 text-left">Order / Medicine</th>
              <th className="p-3 text-left">Prescribed By</th>
              <th className="p-3 text-left">Date</th>
              <th className="p-3 text-left">Status</th>
              <th className="p-3 text-center">Action</th>
            </tr>
          </thead>

          <tbody>
            {loading ? (
              <tr>
                <td colSpan={5} className="p-6 text-center text-gray-400">
                  Loading pharmacy orders...
                </td>
              </tr>
            ) : filteredOrders.length === 0 ? (
              <tr>
                <td colSpan={5} className="p-6 text-center text-gray-400">
                  No pharmacy orders found.
                </td>
              </tr>
            ) : (
              filteredOrders.map((item, idx) => {
                const orderId =
                  item.dispensedId || item.orderId || `Order #${item.id || idx + 1}`;
                const medicines = Array.isArray(item.dispensedDrugsDtl)
                  ? item.dispensedDrugsDtl
                  : [];
                const medNames = medicines
                  .map((m) => m.drugName || m.medicineName)
                  .filter(Boolean)
                  .join(", ");
                const dateVal =
                  item.createdDate ||
                  item.dispensedDate ||
                  item.uploadedDateTime ||
                  item.modifiedDate;
                const status =
                  item.drugIpdStatus ||
                  (item.dispensed_YN === "Y"
                    ? "Dispensed"
                    : item.status === 1
                    ? "Confirm"
                    : "Pending");

                return (
                  <tr
                    key={item.id || item.dispensedId || idx}
                    className="border-t hover:bg-gray-50"
                  >
                    {/* Order / Medicine */}
                    <td className="p-3">
                      <div className="flex flex-col">
                        <span className="font-semibold text-gray-900">
                          {orderId}
                        </span>
                        {medNames ? (
                          <span
                            className="text-[11px] text-gray-500 line-clamp-1 max-w-xs"
                            title={medNames}
                          >
                            {medNames}
                          </span>
                        ) : item.drugName ? (
                          <span className="text-[11px] text-gray-500">
                            {item.drugName}
                          </span>
                        ) : null}
                        {medicines.length > 0 && (
                          <span className="text-[10px] text-teal-600 font-medium">
                            {medicines.length} {medicines.length === 1 ? "drug" : "drugs"}
                          </span>
                        )}
                      </div>
                    </td>

                    {/* Prescribed By */}
                    <td className="p-3 text-gray-700">
                      {item.userName || item.doctorName || item.orderedBy || "-"}
                    </td>

                    {/* Date */}
                    <td className="p-3 text-gray-600">
                      {dateVal ? formatDate(dateVal) : "-"}
                    </td>

                    {/* Status */}
                    <td className="p-3">
                      <span
                        className={`rounded px-2 py-1 text-[10px] font-semibold ${
                          status === "Confirm" || status === "Completed"
                            ? "bg-green-100 text-green-700"
                            : status === "Dispensed"
                            ? "bg-blue-100 text-blue-700"
                            : "bg-orange-100 text-orange-700"
                        }`}
                      >
                        {status}
                      </span>
                    </td>

                    {/* Action */}
                    <td className="p-3">
                      <div className="flex gap-2 justify-center">
                        <button
                          onClick={() => handleViewOrder(item)}
                          className="flex bg-white items-center gap-1 rounded border bg-white px-2.5 py-1.5 text-blue-600 hover:bg-blue-50 transition cursor-pointer shadow-2xs"
                          title="View Order Details"
                        >
                          <FiEye size={13} />
                          <span className="text-[11px] font-medium">View</span>
                        </button>

                        {(item.resultDocumentName || item.documentName) && (
                          <button
                            type="button"
                            onClick={() =>
                              handleOpenFile(
                                item.resultDocumentName || item.documentName
                              )
                            }
                            className="flex items-center gap-1 rounded border bg-white px-2 py-1.5 text-teal-600 hover:bg-teal-50 transition cursor-pointer shadow-2xs"
                            title="View Document"
                          >
                            <FiFileText size={13} />
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>

      {/* Order Details Modal */}
      {selectedOrder && (
        <div
          className="fixed inset-0 z-50 bg-black/60 backdrop-blur-xs flex items-center justify-center p-3 sm:p-5"
          onClick={() => setSelectedOrder(null)}
        >
          <div
            className="bg-white rounded-xl w-full max-w-2xl max-h-[90vh] flex flex-col shadow-2xl overflow-hidden border border-gray-200"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Modal Header */}
            <div className="flex items-center justify-between px-5 py-4 border-b border-gray-200 bg-gray-50">
              <div className="flex items-center gap-2.5">
                <div className="w-8 h-8 rounded-lg bg-indigo-50 border border-indigo-100 flex items-center justify-center">
                  <FaPills className="text-indigo-600 text-sm" />
                </div>
                <div>
                  <h3 className="text-sm font-bold text-gray-900">
                    Pharmacy Order Details
                  </h3>
                  <p className="text-[11px] text-gray-500">
                    Order ID:{" "}
                    <span className="font-semibold text-gray-700">
                      {selectedOrder.dispensedId ||
                        selectedOrder.orderId ||
                        selectedOrder.id ||
                        "-"}
                    </span>
                  </p>
                </div>
              </div>

              <button
                type="button"
                onClick={() => setSelectedOrder(null)}
                className="p-1.5 text-gray-400 hover:text-gray-700 hover:bg-gray-200 rounded-full transition cursor-pointer"
                title="Close"
              >
                <FiX className="text-lg" />
              </button>
            </div>

            {/* Modal Body */}
            <div className="p-5 overflow-y-auto space-y-4 text-xs">
              {/* Order Meta Info Grid */}
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 bg-gray-50 p-3.5 rounded-lg border border-gray-100">
                <div>
                  <span className="text-[10px] text-gray-400 uppercase font-semibold block">
                    Prescribed By
                  </span>
                  <span className="font-medium text-gray-800">
                    {selectedOrder.userName ||
                      selectedOrder.doctorName ||
                      selectedOrder.orderedBy ||
                      "-"}
                  </span>
                </div>
                <div>
                  <span className="text-[10px] text-gray-400 uppercase font-semibold block">
                    Date
                  </span>
                  <span className="font-medium text-gray-800">
                    {selectedOrder.createdDate || selectedOrder.dispensedDate
                      ? formatDate(
                          selectedOrder.createdDate ||
                            selectedOrder.dispensedDate
                        )
                      : "-"}
                  </span>
                </div>
                <div>
                  <span className="text-[10px] text-gray-400 uppercase font-semibold block">
                    Status
                  </span>
                  <span className="inline-block px-2 py-0.5 mt-0.5 rounded text-[10px] font-semibold bg-green-100 text-green-700">
                    {selectedOrder.drugIpdStatus || "Confirm"}
                  </span>
                </div>
                <div>
                  <span className="text-[10px] text-gray-400 uppercase font-semibold block">
                    Total Amount
                  </span>
                  <span className="font-semibold text-gray-900">
                    {selectedOrder.amount || selectedOrder.paidAmount
                      ? `₹${selectedOrder.amount || selectedOrder.paidAmount}`
                      : "-"}
                  </span>
                </div>
              </div>

              {/* Medicines List Table */}
              <div>
                <h4 className="text-xs font-bold text-gray-800 mb-2 flex items-center justify-between">
                  <span>Dispensed Medicines</span>
                  <span className="text-[11px] font-normal text-gray-500">
                    {Array.isArray(selectedOrder.dispensedDrugsDtl)
                      ? selectedOrder.dispensedDrugsDtl.length
                      : 0}{" "}
                    item(s)
                  </span>
                </h4>

                {Array.isArray(selectedOrder.dispensedDrugsDtl) &&
                selectedOrder.dispensedDrugsDtl.length > 0 ? (
                  <div className="border border-gray-200 rounded-lg overflow-hidden">
                    <table className="min-w-full text-xs">
                      <thead className="bg-gray-100 text-gray-700">
                        <tr>
                          <th className="p-2.5 text-left font-semibold">Drug Name</th>
                          <th className="p-2.5 text-left font-semibold">Dosage / Freq</th>
                          <th className="p-2.5 text-center font-semibold">Qty</th>
                          <th className="p-2.5 text-left font-semibold">Instructions</th>
                          <th className="p-2.5 text-right font-semibold">Amount</th>
                        </tr>
                      </thead>
                      <tbody>
                        {selectedOrder.dispensedDrugsDtl.map((med, mIdx) => (
                          <tr
                            key={mIdx}
                            className="border-t border-gray-100 hover:bg-gray-50"
                          >
                            <td className="p-2.5 font-medium text-gray-900">
                              {med.drugName || med.medicineName || "-"}
                            </td>
                            <td className="p-2.5 text-gray-600">
                              {[med.dosage || med.dose, med.frequency]
                                .filter(Boolean)
                                .join(" • ") || "-"}
                            </td>
                            <td className="p-2.5 text-center font-semibold text-gray-800">
                              {med.quantity || med.qty || med.quantity || "-"}
                            </td>
                            <td className="p-2.5 text-gray-500">
                              {med.instruction || med.route || "-"}
                            </td>
                            <td className="p-2.5 text-right font-medium text-gray-800">
                              {med.amount ? `₹${med.amount}` : "-"}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                ) : (
                  <p className="text-gray-400 italic py-2">
                    No individual drug details found.
                  </p>
                )}
              </div>

              {/* Uploaded Document / Prescription Link */}
              {(selectedOrder.resultDocumentName ||
                selectedOrder.documentName) && (
                <div className="flex items-center justify-between p-3 bg-teal-50 border border-teal-200 rounded-lg">
                  <div className="flex items-center gap-2">
                    <FiFileText className="text-teal-700 text-base" />
                    <div>
                      <p className="font-semibold text-teal-900 text-xs">
                        Prescription Document Attached
                      </p>
                      <p className="text-[11px] text-teal-700">
                        {selectedOrder.resultDocumentName ||
                          selectedOrder.documentName}
                      </p>
                    </div>
                  </div>
                  <button
                    type="button"
                    onClick={() =>
                      handleOpenFile(
                        selectedOrder.resultDocumentName ||
                          selectedOrder.documentName
                      )
                    }
                    className="flex items-center gap-1.5 px-3 py-1.5 bg-teal-600 hover:bg-teal-700 text-white rounded-md text-xs font-semibold transition cursor-pointer shadow-2xs"
                  >
                    <FiEye size={12} />
                    View File
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
